import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, Menu, X, Building2, Briefcase, GraduationCap, FileText, Mail, Users, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { prefetchRoute } from '@/utils/prefetchRoute';
import logo from '@/assets/logo-nucleo.svg';
import citIcon from '@/assets/icons/cit-icon.svg';
import flagBR from '@/assets/flags/br.png';
import flagUS from '@/assets/flags/us.png';
import flagES from '@/assets/flags/es.png';

const languages = [
  { code: 'pt', label: 'Português', flag: flagBR },
  { code: 'en', label: 'English', flag: flagUS },
  { code: 'es', label: 'Español', flag: flagES },
];

const LanguageSelector = () => {
  const [open, setOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { i18n } = useTranslation();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const currentCode = i18n.language?.substring(0, 2) || 'pt';

  return (
    <div ref={ref} className="relative h-full flex items-center">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-11 px-4 rounded-full border border-foreground/20 text-foreground text-sm font-medium hover:border-primary hover:text-primary transition-colors duration-300"
      >
        <Globe className="w-4 h-4" />
        <span className="uppercase">{currentCode}</span>
        <span
          className="inline-flex transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </button>

      <div
        className="absolute right-0 w-48 bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-border/40 overflow-hidden z-[1200] transition-all duration-200 ease-out"
        style={{
          ...(isMobile
            ? { bottom: 'calc(100% + 14px)' }
            : { top: 'calc(100% + 14px)' }),
          opacity: open ? 1 : 0,
          transform: open
            ? 'translateY(0) scale(1)'
            : isMobile
              ? 'translateY(8px) scale(0.95)'
              : 'translateY(-8px) scale(0.95)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <div className="py-2">
          {languages.map((lang, idx) => (
            <button
              key={lang.code}
              className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                currentCode === lang.code ? 'text-primary bg-primary/5' : 'text-foreground hover:bg-secondary hover:text-primary'
              }`}
              onClick={() => { i18n.changeLanguage(lang.code); setOpen(false); }}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <span className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center overflow-hidden">
                <img src={lang.flag} alt={lang.label} decoding="async" width={28} height={28} className="w-full h-full object-cover" />
              </span>
              <span className="flex-1 text-left">{lang.label}</span>
              <span
                className="transition-all duration-150"
                style={{
                  opacity: hoveredIdx === idx ? 1 : 0,
                  transform: hoveredIdx === idx ? 'translateX(0)' : 'translateX(-6px)',
                }}
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

interface NavItemProps {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isGold?: boolean;
}

const NavItem = ({ href, icon, children, isGold = false }: NavItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const isExternal = href.startsWith('#') || href.startsWith('http');

  const content = (
    <>
      <span
        className="flex items-center justify-center overflow-hidden text-primary"
        style={{
          width: isHovered ? 28 : 0,
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'scale(1)' : 'scale(0.2)',
          marginRight: isHovered ? 8 : 0,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {icon}
      </span>
      <span className="relative">
        {children}
        <span
          className={`absolute -bottom-1 left-0 h-0.5 ${isGold ? 'bg-gold' : 'bg-primary'}`}
          style={{
            width: isHovered ? '100%' : '0%',
            transition: 'width 0.3s ease-out',
          }}
        />
      </span>
      <span
        className="absolute inset-0 -z-10 rounded-full bg-primary/5"
        style={{
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.2s ease',
          margin: '-4px -12px',
        }}
      />
    </>
  );

  const className = `relative flex items-center text-sm lg:text-base font-medium transition-colors duration-300 py-2 px-1 ${
    isGold
      ? 'text-gold hover:text-gold/80 font-semibold'
      : 'text-text-gray hover:text-primary'
  }`;

  if (isExternal) {
    return (
      <a href={href} className={className} onMouseEnter={() => { setIsHovered(true); prefetchRoute(href); }} onMouseLeave={() => setIsHovered(false)}>
        {content}
      </a>
    );
  }

  return (
    <Link to={href} className={className} onMouseEnter={() => { setIsHovered(true); prefetchRoute(href); }} onMouseLeave={() => setIsHovered(false)}>
      {content}
    </Link>
  );
};

interface ServicesDropdownProps {
  isHovered: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
}

const ServicesDropdown = ({ isHovered, onHoverStart, onHoverEnd }: ServicesDropdownProps) => {
  const { t } = useTranslation();

  const servicesLeft = [
    { title: t('megaMenu.nucleoInteligencia'), desc: t('megaMenu.nucleoInteligenciaDesc'), href: '/solucoes/reducao-tributaria' },
    { title: t('megaMenu.solucoesContabeis'), desc: t('megaMenu.solucoesContabeisDesc'), href: '/solucoes/assessoria-contabil' },
    { title: t('megaMenu.bpoFinanceiro'), desc: t('megaMenu.bpoFinanceiroDesc'), href: '/solucoes/bpo-financeiro' },
    { title: t('megaMenu.solucoesDigitais'), desc: t('megaMenu.solucoesDigitaisDesc'), href: '/solucoes/assessoria-bdmg' },
  ];

  const servicesRight = [
    { title: t('megaMenu.coreCerti'), desc: t('megaMenu.coreCertiDesc'), href: '/solucoes/certificado-digital' },
    { title: t('megaMenu.contPay'), desc: t('megaMenu.contPayDesc'), href: '/solucoes/contpay' },
    { title: t('megaMenu.coremig'), desc: t('megaMenu.coremigDesc'), href: '/solucoes/energia' },
  ];

  return (
    <div
      className="relative h-full flex items-center"
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <button className="flex items-center text-sm lg:text-base font-medium text-text-gray hover:text-primary transition-colors duration-300 py-2 px-1" aria-label="Soluções" aria-expanded={isHovered}>
        <span
          className="flex items-center justify-center overflow-hidden text-primary"
          style={{
            width: isHovered ? 28 : 0,
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'scale(1)' : 'scale(0.2)',
            marginRight: isHovered ? 8 : 0,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Briefcase className="w-5 h-5" />
        </span>

        <span>{t('navbar.solucoes')}</span>

        <span
          className="inline-flex ml-1 transition-transform duration-300"
          style={{ transform: isHovered ? 'rotate(180deg)' : 'rotate(0)' }}
        >
          <ChevronDown className="w-4 h-4" />
        </span>
      </button>

      <div
        className="absolute left-1/2 w-[680px] bg-white rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.1)] z-[1100] grid grid-cols-2 gap-8 transition-all duration-200 ease-out"
        style={{
          top: 'calc(100% + 14px)',
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'translate(-50%, 0)' : 'translate(-50%, -10px)',
          pointerEvents: isHovered ? 'auto' : 'none',
        }}
      >
        <div className="absolute -top-4 left-0 w-full h-4 bg-transparent" />

        <div className="flex flex-col gap-3">
          {servicesLeft.map((service) => (
            <Link key={service.title} to={service.href} className="block p-3 rounded-xl hover:bg-secondary transition-colors" onMouseEnter={() => prefetchRoute(service.href)}>
              <span className="block text-[0.9rem] font-semibold text-foreground hover:text-primary mb-1">{service.title}</span>
              <span className="block text-[0.8rem] text-text-light-gray leading-snug">{service.desc}</span>
            </Link>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          {servicesRight.map((service) => (
            <Link key={service.title} to={service.href} className="block p-3 rounded-xl hover:bg-secondary transition-colors" onMouseEnter={() => prefetchRoute(service.href)}>
              <span className="block text-[0.9rem] font-semibold text-foreground hover:text-primary mb-1">{service.title}</span>
              <span className="block text-[0.8rem] text-text-light-gray leading-snug">{service.desc}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [servicesHovered, setServicesHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    // Defer scroll listener to avoid blocking first paint
    const rafId = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const mobileServices = [
    { title: t('megaMenu.nucleoInteligencia'), href: '/solucoes/reducao-tributaria' },
    { title: t('megaMenu.solucoesContabeis'), href: '/solucoes/assessoria-contabil' },
    { title: t('megaMenu.bpoFinanceiro'), href: '/solucoes/bpo-financeiro' },
    { title: t('megaMenu.solucoesDigitais'), href: '/solucoes/assessoria-bdmg' },
    { title: t('megaMenu.coreCerti'), href: '/solucoes/certificado-digital' },
    { title: t('megaMenu.contPay'), href: '/solucoes/contpay' },
    { title: t('megaMenu.coremig'), href: '/solucoes/energia' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-[1000] pointer-events-none transition-all duration-300 animate-navbar-slidedown ${
        isScrolled ? 'pt-3' : 'pt-6'
      }`}
    >
      <nav
        className={`pointer-events-auto flex items-center justify-between w-[94%] max-w-[1400px] mx-auto h-[72px] md:h-[88px] px-5 md:px-10 rounded-full transition-all duration-300 ${
          isScrolled
            ? 'bg-[#CDE3F3]/95 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-white/20'
            : 'bg-[#CDE3F3] shadow-[0_10px_40px_rgba(0,0,0,0.08)]'
        }`}
      >
        {/* Logo */}
        <div className="flex-1 flex items-center">
          <Link to="/">
            <img src={logo} alt="Núcleo" width={48} height={48} className="h-12 w-auto" fetchPriority="high" />
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex flex-[3] justify-end gap-3 lg:gap-8 h-full items-center pr-4 lg:pr-8">
          <NavItem href="/a-nucleo" icon={<Building2 className="w-5 h-5" />}>
            {t('navbar.aNucleo')}
          </NavItem>

          <ServicesDropdown
            isHovered={servicesHovered}
            onHoverStart={() => setServicesHovered(true)}
            onHoverEnd={() => setServicesHovered(false)}
          />

          <NavItem href="/cit" icon={<img src={citIcon} alt="CIT" decoding="async" width={20} height={20} className="w-5 h-5" />}>
            {t('navbar.cit')}
          </NavItem>

          <NavItem href="/academy" icon={<GraduationCap className="w-5 h-5" />}>
            {t('navbar.academy')}
          </NavItem>

          <NavItem href="/blog" icon={<FileText className="w-5 h-5" />}>
            {t('navbar.blog')}
          </NavItem>

          <NavItem href="/faca-parte" icon={<Users className="w-5 h-5" />}>
            {t('navbar.facaParte')}
          </NavItem>

          <NavItem href="#contato" icon={<Mail className="w-5 h-5" />}>
            {t('navbar.contato')}
          </NavItem>
        </div>

        {/* Intranet Button + Language Selector */}
        <div className="hidden lg:flex flex-1 justify-end items-center gap-2 lg:gap-3 h-full">
          <Link
            to="/intranet"
            className="relative flex items-center justify-between h-11 pl-5 rounded-full overflow-hidden group"
          >
            <span className="absolute top-0 right-0 bottom-0 w-11 bg-primary rounded-full transition-all duration-[800ms] ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:w-full z-0" />
            <span className="relative z-10 text-[0.95rem] font-semibold text-foreground mr-3 transition-colors duration-300 group-hover:text-secondary">
              {t('navbar.intranet')}
            </span>
            <span className="relative z-10 w-11 h-11 flex items-center justify-center text-secondary">
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
          <LanguageSelector />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-foreground min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className="lg:hidden pointer-events-auto absolute top-full mt-4 left-3 right-3 bg-white rounded-2xl p-6 shadow-xl overflow-hidden z-[1100] transition-all duration-300 ease-out"
        style={{
          opacity: mobileMenuOpen ? 1 : 0,
          transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-20px)',
          pointerEvents: mobileMenuOpen ? 'auto' : 'none',
        }}
      >
        <div className="flex flex-col gap-1">
          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.05s both' : 'none' }}>
            <Link to="/a-nucleo" className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2" onClick={() => setMobileMenuOpen(false)}>
              <span className="text-primary"><Building2 className="w-5 h-5" /></span>
              {t('navbar.aNucleo')}
            </Link>
          </div>

          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.1s both' : 'none' }}>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="flex items-center justify-between w-full text-base font-medium text-foreground min-h-[44px] py-2"
            >
              <span className="flex items-center gap-3">
                <span className="text-primary"><Briefcase className="w-5 h-5" /></span>
                {t('navbar.solucoes')}
              </span>
              <span
                className="inline-flex transition-transform duration-300"
                style={{ transform: mobileServicesOpen ? 'rotate(180deg)' : 'rotate(0)' }}
              >
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </span>
            </button>

            <div
              className="overflow-hidden transition-all duration-300 ease-out"
              style={{
                display: 'grid',
                gridTemplateRows: mobileServicesOpen ? '1fr' : '0fr',
              }}
            >
              <div className="overflow-hidden">
                <div className="pl-8 pt-3 flex flex-col gap-2">
                  {mobileServices.map((service) => (
                    <Link key={service.href} to={service.href} className="block py-2.5 min-h-[44px] flex items-center text-sm text-text-gray hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)} onTouchStart={() => prefetchRoute(service.href)}>
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.15s both' : 'none' }}>
            <Link to="/cit" className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2" onClick={() => setMobileMenuOpen(false)}>
              <span className="text-primary"><img src={citIcon} alt="CIT" decoding="async" width={20} height={20} className="w-5 h-5" /></span>
              {t('navbar.cit')}
            </Link>
          </div>

          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.2s both' : 'none' }}>
            <Link to="/academy" className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2" onClick={() => setMobileMenuOpen(false)}>
              <span className="text-primary"><GraduationCap className="w-5 h-5" /></span>
              {t('navbar.academy')}
            </Link>
          </div>

          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.25s both' : 'none' }}>
            <Link to="/blog" className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2" onClick={() => setMobileMenuOpen(false)}>
              <span className="text-primary"><FileText className="w-5 h-5" /></span>
              {t('navbar.blog')}
            </Link>
          </div>

          <div style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.28s both' : 'none' }}>
            <Link to="/faca-parte" className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2" onClick={() => setMobileMenuOpen(false)}>
              <span className="text-primary"><Users className="w-5 h-5" /></span>
              {t('navbar.facaParte')}
            </Link>
          </div>

          <a
            href="#contato"
            className="flex items-center gap-3 text-base font-medium text-foreground min-h-[44px] py-2"
            style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.32s both' : 'none' }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="text-primary"><Mail className="w-5 h-5" /></span>
            {t('navbar.contato')}
          </a>

          <hr className="my-2 border-border" />

          <div
            className="flex items-center justify-between gap-3"
            style={{ animation: mobileMenuOpen ? 'mobile-item-fadein 0.2s ease 0.25s both' : 'none' }}
          >
            <Link
              to="/intranet"
              className="inline-flex items-center justify-center gap-2 flex-1 h-11 bg-primary text-secondary rounded-full font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('navbar.intranet')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </div>
    </header>
  );
};
